<!--
 * @Description: 开户信息页，各种状态都在此显示。
                  当前是否可开户完全按照接口返回状态执行
 * @Date: 2025-05-19 18:01:07
-->
<template>
  <div class="account-info">
    <div style="box-sizing: border-box; padding: 20px 0">
      <BaseSteps
        finish-status="success"
        simple
        :active="step.active"
        :process-status="step.processStatus"
      >
        <BaseStep
          v-for="(item, index) in steps"
          :key="index"
          :title="item"
        ></BaseStep>
      </BaseSteps>
    </div>
    <div style="height: 16px; background: #f2f4f8"></div>

    <BaseScrollbar always>
      <BaseForm
        ref="formRef"
        label-position="top"
        :disabled="disabled"
        :model="formData"
      >
        <!-- 电子账簿信息 -->
        <AccountInfoBook v-if="bank.platformAuthStatus === 6" />
        <div style="height: 16px; background: #f2f4f8"></div>

        <!-- 富友支付开户银行 -->
        <AccountInfoFy />
        <div style="height: 16px; background: #f2f4f8"></div>

        <!-- 已开户展示 -->
        <template>
          <AccountInfoInout />
          <AccountInfoElectron />
        </template>

        <!-- 公司信息 -->
        <AccountInfoCompany />
        <div style="height: 16px; background: #f2f4f8"></div>

        <!-- 法人信息 -->
        <AccountInfoLegal />
        <div style="height: 16px; background: #f2f4f8"></div>

        <!-- 银行信息 -->
        <AccountInfoBank />
        <div style="height: 16px; background: #f2f4f8"></div>

        <!-- 经办人信息 -->
        <AccountInfoHandled />
        <div style="height: 16px; background: #f2f4f8"></div>

        <template v-if="['10', '12'].includes(formData.fyChannel)">
          <!-- 收益人信息 -->
          <AccountInfoBeneficiary />
          <div style="height: 16px; background: #f2f4f8"></div>
          <!-- 股东信息 -->
          <AccountInfoShareholder />
        </template>
      </BaseForm>

      <!-- 向日葵审核记录、支付通道审核记录 -->
      <template v-for="item in AUTH_LIST_FIELD" :key="item.label + Date.now()">
        <div
          v-if="authLists[item.value]?.length"
          style="border-top: 16px solid #f2f4f8"
        >
          <BaseTextTitle class="account-info__items__title">
            {{ item.label }}
          </BaseTextTitle>
          <BaseTextContent>
            <BaseTable :columns="columns" :data="authLists[item.value]" />
          </BaseTextContent>
        </div>
      </template>

      <div style="text-align: center">
        <BaseCheckbox
          v-if="!disabled"
          style="margin-bottom: 24px"
          v-model="agree"
          :trueValue="1"
          :falseValue="0"
        >
          <div style="display: flex; align-items: center">
            <span>我同意遵守</span>
            <BaseLink
              type="primary"
              href="//cdn.yxvzb.com/WEB/SaaS/agreement/fy/富友-向日葵平台专用账户协议.docx"
              target="_blank"
            >
              《富友支付开户三方协议》
            </BaseLink>
          </div>
        </BaseCheckbox>
      </div>
    </BaseScrollbar>

    <div class="affix">
      <div v-if="!disabled">
        <BaseButton type="default" @click="onCancel">取消</BaseButton>
        <BaseButton :disabled="!agree" :click="onSubmit">提交</BaseButton>
      </div>
      <div v-else>
        <BaseButton type="default" @click="onCancel">返回</BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import {
  BaseForm,
  BaseButton,
  BaseSteps,
  BaseStep,
  BaseTableProps,
  useTableColumn,
  BaseCheckbox,
  BaseTable,
  BaseTextContent,
  BaseTextTitle,
  BaseLink,
  BaseScrollbar
} from 'xrk-components';
import {
  AccountInfoBook,
  AccountInfoInout,
  AccountInfoElectron,
  AccountInfoFy,
  AccountInfoCompany,
  AccountInfoLegal,
  AccountInfoBank,
  AccountInfoHandled,
  AccountInfoBeneficiary,
  AccountInfoShareholder
} from '../components/account-info';
import infoStore from '../store/info';
import { storeToRefs } from 'pinia';
import { useRoute, LocationQuery, useRouter } from 'vue-router';
import { PayPassagewayCode, AuthStatus } from '@/common/types/bank';
import { formatter } from '@/utils';
import { IS_PLATFORM_TYPE_1 } from '@/config';

interface RouteQuery extends LocationQuery {
  payPassagewayCode: PayPassagewayCode;
}

const AUTH_RESP_STATUS = {
  1: {
    label: '审核中',
    value: 1
  },
  2: {
    label: '通过',
    value: 2
  },
  3: {
    label: '不通过',
    value: 3
  }
};
const AUTH_LIST_FIELD = [
  {
    label: IS_PLATFORM_TYPE_1 ? '向日葵审核记录' : '审核记录',
    value: 'platformAuthList'
  },
  {
    label: '支付通道审核记录',
    value: 'bankAuthList'
  }
];
const AUDITH_STEP: Record<AuthStatus, any> = {
  0: { active: 0, processStatus: 'process', title: '企业信息录入' },
  1: { active: 1, processStatus: 'process', title: '企业信息人工审核' },
  2: { active: 1, processStatus: 'error', title: '企业信息人工审核' },
  3: { active: 1, processStatus: 'success', title: '企业信息人工审核' },
  4: { active: 2, processStatus: 'process', title: '银行审核' },
  5: { active: 2, processStatus: 'error', title: '银行审核' },
  6: { active: 3, processStatus: 'success', title: '开通成功' },
  7: { active: 3, processStatus: 'error', title: '已关闭' }
};

const routeQuery = useRoute().query as RouteQuery;
const router = useRouter();

const store = infoStore();
const { authLists, formData, agree, bank, disabled, formRef } =
  storeToRefs(store);
const { onSubmit, init, setPayPassagewayCode } = store;

const step = ref<{
  active: number;
  processStatus: 'wait' | 'process' | 'finish' | 'error' | 'success';
}>({
  active: 0,
  processStatus: 'process'
});
const steps = ref([]);

const columns = ref<BaseTableProps['columns']>([
  useTableColumn({
    label: '审核结果',
    prop: 'authStatus',
    formatter: ({ cellValue }) =>
      formatter.defaultFormat(
        cellValue,
        () => AUTH_RESP_STATUS[cellValue].label
      )
  }),
  useTableColumn({
    label: '原因',
    prop: 'auditRemark',
    formatter: ({ cellValue }) => formatter.defaultFormat(cellValue)
  }),
  useTableColumn({
    label: '时间',
    prop: 'auditAt',
    formatter: ({ cellValue }) => formatter.date(cellValue)
  })
]);

watch(
  () => bank.value,
  v => {
    steps.value = [
      AUDITH_STEP[0].title,
      AUDITH_STEP[1].title,
      AUDITH_STEP[4].title,
      v.platformAuthStatus === 7
        ? AUDITH_STEP['7'].title
        : AUDITH_STEP['6'].title
    ];

    step.value = {
      active: AUDITH_STEP[v.platformAuthStatus].active,
      processStatus: AUDITH_STEP[v.platformAuthStatus].processStatus
    };
  }
);

const onCancel = () => {
  router.go(-1);
};

setPayPassagewayCode(routeQuery.payPassagewayCode);
init();
</script>
<style lang="scss">
@import '../styles/account-info.scss';
</style>
